// Error Message Utility
// Maps API error codes/messages to user-friendly messages

/**
 * Common error codes and their user-friendly messages
 */
const ERROR_MESSAGES = {
  // Authentication errors
  'invalid_credentials': 'Invalid email or password. Please try again.',
  'wrong_password': 'Incorrect password. Please try again.',
  // User not found errors (all map to same message)
  'user_not_found': 'No account found with this email. Please sign up.',
  'account_not_found': 'No account found with this email. Please sign up.',
  'email_not_found': 'No account found with this email. Please sign up.',
  'invalid_email': 'Please enter a valid email address.',
  'invalid_password': 'Password is incorrect. Please try again.',
  
  // Account status errors
  'account_disabled': 'Your account has been disabled. Please contact support.',
  'account_locked': 'Your account is locked. Please try again later or contact support.',
  'account_suspended': 'Your account has been suspended. Please contact support.',
  'account_inactive': 'Your account is inactive. Please contact admin to reactivate.',
  'not_approved': 'Your account is pending approval.',
  
  // Registration errors
  // Email exists errors (consolidated to one message)
  'email_exists': 'An account with this email already exists. Please login instead.',
  'email_already_exists': 'An account with this email already exists. Please login instead.',
  'duplicate_email': 'An account with this email already exists. Please login instead.',
  // Phone exists errors (consolidated to one message)
  'phone_exists': 'An account with this phone number already exists.',
  'phone_already_exists': 'An account with this phone number already exists.',
  'duplicate_phone': 'An account with this phone number already exists.',
  'user_exists': 'An account with these details already exists. Please login.',
  'duplicate_entry': 'An account with these details already exists.',
  
  // Validation errors
  'invalid_email_format': 'Please enter a valid email address.',
  'invalid_phone_format': 'Please enter a valid phone number.',
  'password_too_short': 'Password must be at least 8 characters long.',
  'password_too_weak': 'Password is too weak. Use letters, numbers, and symbols.',
  'missing_required_fields': 'Please fill in all required fields.',
  'invalid_input': 'Please check your input and try again.',
  
  // Network/Server errors
  'network_error': 'Unable to connect. Please check your internet connection.',
  'server_error': 'Something went wrong on our end. Please try again later.',
  'timeout': 'Request timed out. Please try again.',
  'service_unavailable': 'Service is temporarily unavailable. Please try again later.',
  
  // Rate limiting
  'too_many_requests': 'Too many attempts. Please wait a few minutes and try again.',
  'rate_limited': 'Too many attempts. Please wait a few minutes and try again.',
};

/**
 * Parse API error and return user-friendly message
 * @param {object|string} error - Error from API
 * @param {string} defaultMessage - Default message if no match found
 * @returns {string} User-friendly error message
 */
export const getErrorMessage = (error, defaultMessage = 'Something went wrong. Please try again.') => {
  if (!error) return defaultMessage;

  // If error is a string, check if it matches any known error
  if (typeof error === 'string') {
    const lowerError = error.toLowerCase();
    
    // Check for exact match first
    if (ERROR_MESSAGES[lowerError]) {
      return ERROR_MESSAGES[lowerError];
    }
    
    // Check for partial matches
    for (const [key, message] of Object.entries(ERROR_MESSAGES)) {
      if (lowerError.includes(key.replace(/_/g, ' ')) || lowerError.includes(key)) {
        return message;
      }
    }
    
    // Check for common patterns in error strings
    if (lowerError.includes('already exist') || lowerError.includes('duplicate')) {
      if (lowerError.includes('email')) {
        return ERROR_MESSAGES['email_exists'];
      }
      if (lowerError.includes('phone')) {
        return ERROR_MESSAGES['phone_exists'];
      }
      return 'An account with these details already exists.';
    }
    
    if (lowerError.includes('not found') || lowerError.includes('no user')) {
      return ERROR_MESSAGES['user_not_found'];
    }
    
    if (lowerError.includes('invalid') && lowerError.includes('credential')) {
      return ERROR_MESSAGES['invalid_credentials'];
    }
    
    if (lowerError.includes('incorrect') && lowerError.includes('password')) {
      return ERROR_MESSAGES['wrong_password'];
    }
    
    if (lowerError.includes('disabled') || lowerError.includes('deactivated')) {
      return ERROR_MESSAGES['account_disabled'];
    }
    
    if (lowerError.includes('locked')) {
      return ERROR_MESSAGES['account_locked'];
    }
    
    if (lowerError.includes('network') || lowerError.includes('connection')) {
      return ERROR_MESSAGES['network_error'];
    }
    
    if (lowerError.includes('timeout')) {
      return ERROR_MESSAGES['timeout'];
    }
    
    // Return the original error if it's user-friendly enough
    if (error.length < 100 && !lowerError.includes('exception') && !lowerError.includes('error:')) {
      return error;
    }
    
    return defaultMessage;
  }

  // If error is an object
  if (typeof error === 'object') {
    // Check for error code
    const errorCode = error.code || error.error_code || error.errorCode;
    if (errorCode && ERROR_MESSAGES[errorCode.toLowerCase()]) {
      return ERROR_MESSAGES[errorCode.toLowerCase()];
    }
    
    // Check for message
    const message = error.message || error.msg || error.error || error.detail;
    if (message) {
      return getErrorMessage(message, defaultMessage);
    }
    
    // Check for errors array
    if (Array.isArray(error.errors) && error.errors.length > 0) {
      return getErrorMessage(error.errors[0], defaultMessage);
    }
  }

  return defaultMessage;
};

/**
 * Get login-specific error message
 * @param {object|string} error - Error from API
 * @returns {string} User-friendly login error message
 */
export const getLoginErrorMessage = (error) => {
  return getErrorMessage(error, 'Login failed. Please check your credentials and try again.');
};

/**
 * Get signup-specific error message
 * @param {object|string} error - Error from API
 * @returns {string} User-friendly signup error message
 */
export const getSignupErrorMessage = (error) => {
  return getErrorMessage(error, 'Registration failed. Please try again.');
};

/**
 * Check if error is a network error
 * @param {object|string} error - Error from API
 * @returns {boolean}
 */
export const isNetworkError = (error) => {
  if (!error) return false;
  
  const errorStr = typeof error === 'string' ? error : (error.message || error.error || '');
  const lowerError = errorStr.toLowerCase();
  
  return lowerError.includes('network') || 
         lowerError.includes('connection') || 
         lowerError.includes('timeout') ||
         lowerError.includes('unavailable') ||
         lowerError.includes('failed to fetch');
};

export default {
  getErrorMessage,
  getLoginErrorMessage,
  getSignupErrorMessage,
  isNetworkError,
};

